import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Table, Button, Form, Row, Col } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaTimes, FaSave } from 'react-icons/fa';
import Select from 'react-select';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import SelectSearchInput from '../../SelectSearch';
import { toast } from 'react-toastify';

const SubRecipeCost = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const [unitDefinitions, setUnitDefinitions] = useState([]);
    const [token, setToken] = useState(sessionStorage.getItem('token') || "");
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [costItems, setCostItems] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);

    const recipeNameRef = useRef(null);
    const recipeUomRef = useRef(null);
    const recipeUnitRef = useRef(null);
    const ingredientSelectRef = useRef(null);
    const quantityRef = useRef(null);
    const uomSelectRef = useRef(null);

    const [formData, setFormData] = useState({
        name: '',
        uom: '',
        unit: '',
        costItem: '',
        rate: '',
        itemUnit: '',
        itemUom: '',
        quantity: '',
        new_uom: '',
    });

    const kgVariations = useMemo(() => ['kg', 'kgs', 'kilogram', 'kilograms'], []);
    const gramVariations = useMemo(() => ['g', 'gram', 'grams', 'gm', 'gms', 'grm', 'grms'], []);
    const literVariations = useMemo(() => ['l', 'ltr', 'ltrs', 'liter', 'liters', 'litre', 'litres'], []);
    const milliliterVariations = useMemo(() => ['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres', 'mls'], []);

    const fetchUnitDefinitions = useCallback(async () => {
        if (!token || !selectedOutlet) return;

        try {
            const response = await axios.get(`${baseUrl}/unit-definitions`, {
                params: {
                    token: token,
                    outlet: selectedOutlet
                }
            });
            setUnitDefinitions(response.data);
        } catch (error) {
            console.error('Error fetching unit definitions:', error);
            toast.error('Failed to fetch unit definitions');
        }
    }, [token, selectedOutlet, baseUrl]);

    useEffect(() => {
        fetchUnitDefinitions();
    }, [fetchUnitDefinitions]);

    useEffect(() => {
        if (recipeNameRef.current) {
            recipeNameRef.current.focus();
        }
    }, [costItems]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if ((name === 'unit' || name === 'quantity')) {
            if (value && isNaN(value)) return;
        }
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleKeyDown = (e, nextRef) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (nextRef && nextRef.current) {
                nextRef.current.focus();
            } else {
                handleAddCostItem(e);
            }
        }
    };

    const normalizeUom = useCallback((uom) => {
        if (!uom) return '';
        const lowerUom = uom.toLowerCase().trim();

        if (kgVariations.includes(lowerUom)) return 'kg';
        if (gramVariations.includes(lowerUom)) return 'g';
        if (literVariations.includes(lowerUom)) return 'ltr';
        if (milliliterVariations.includes(lowerUom)) return 'ml';

        if (lowerUom === 'grm') return 'g';
        if (lowerUom === 'l') return 'ltr';

        return lowerUom;
    }, [kgVariations, gramVariations, literVariations, milliliterVariations]);

    const handleCostItemSelect = (selectedOption) => {
        const itemName = selectedOption ? selectedOption.value : '';
        const selectedItem = unitDefinitions.find(item => item.name === itemName);

        if (selectedItem) {
            const normalizedUom = normalizeUom(selectedItem.uom);
            const showConversionOptions = ['kg', 'g', 'ltr', 'ml'].includes(normalizedUom);

            setFormData(prev => ({
                ...prev,
                costItem: selectedItem.name,
                rate: selectedItem.rate || '',
                itemUnit: selectedItem.unit || '',
                itemUom: normalizedUom,
                new_uom: showConversionOptions ? '' : normalizedUom
            }));

            setTimeout(() => {
                if (quantityRef.current) {
                    quantityRef.current.focus();
                }
            }, 100);
        }
    };

    const handleUomChange = (selectedOption) => {
        const new_uom = selectedOption ? selectedOption.value : '';
        setFormData(prev => ({
            ...prev,
            new_uom
        }));

        setTimeout(() => {
            const addButton = document.querySelector('button[type="submit"]');
            if (addButton) {
                addButton.focus();
            }
        }, 100);
    };

    const handleAddCostItem = (e) => {
        e.preventDefault();

        if (!formData.name || !formData.costItem || !formData.quantity ||
            (['kg', 'ltr'].includes(formData.itemUom) && !formData.new_uom)) {
            toast.error('Please fill all required fields');
            return;
        }

        const quantity = parseFloat(formData.quantity);
        if (isNaN(quantity) || quantity <= 0) {
            toast.error('Please enter a valid quantity');
            return;
        }

        const exists = costItems.some(
            (item, idx) => item.costItem === formData.costItem && idx !== editingIndex
        );
        if (exists && editingIndex === null) {
            toast.info('This ingredient already exists');
            return;
        }

        let calculatedUnit = parseFloat(formData.itemUnit) || 0;
        let calculatedQuantity = quantity;
        let rate = parseFloat(formData.rate) || 0;
        let costPrice = 0;

        if (formData.new_uom === 'g' && formData.itemUom === 'kg') {
            calculatedUnit *= 1000;
        } else if (formData.new_uom === 'kg' && formData.itemUom === 'g') {
            calculatedUnit /= 1000;
        } else if (formData.new_uom === 'ml' && formData.itemUom === 'ltr') {
            calculatedUnit *= 1000;
        } else if (formData.new_uom === 'ltr' && formData.itemUom === 'ml') {
            calculatedUnit /= 1000;
        }

        if (calculatedUnit > 0) {
            costPrice = (calculatedQuantity / calculatedUnit) * rate;
        }

        const newCostItem = {
            name: formData.costItem,
            rate: formData.rate,
            uom: formData.itemUom,
            new_uom: formData.new_uom || formData.itemUom,
            quantity: quantity.toString(),
            cost: costPrice.toFixed(2),
            unit: formData.unit,
            itemUnit: formData.itemUnit,
            itemUom: formData.itemUom
        };

        if (editingIndex !== null) {
            const updatedItems = [...costItems];
            updatedItems[editingIndex] = newCostItem;
            setCostItems(updatedItems);
            setEditingIndex(null);
        } else {
            setCostItems([...costItems, newCostItem]);
        }

        setFormData(prev => ({
            ...prev,
            costItem: '',
            rate: '',
            itemUnit: '',
            itemUom: '',
            quantity: '',
            new_uom: '',
        }));

        setTimeout(() => {
            if (ingredientSelectRef.current) {
                ingredientSelectRef.current.focus();
            }
        }, 100);
    };

    const handleEdit = (index) => {
        if (index < 0 || index >= costItems.length) return;

        setEditingIndex(index);
        const item = costItems[index];
        setFormData(prev => ({
            ...prev,
            costItem: item.name,
            rate: item.rate,
            itemUnit: item.itemUnit,
            itemUom: item.itemUom,
            quantity: item.quantity.toString(),
            new_uom: item.new_uom
        }));

        setTimeout(() => {
            if (quantityRef.current) {
                quantityRef.current.focus();
            }
        }, 100);
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setFormData(prev => ({
            ...prev,
            costItem: '',
            rate: '',
            itemUnit: '',
            itemUom: '',
            quantity: '',
            new_uom: '',
        }));

        setTimeout(() => {
            if (ingredientSelectRef.current) {
                ingredientSelectRef.current.focus();
            }
        }, 100);
    };

    const handleDelete = (index) => {
        if (index < 0 || index >= costItems.length) return;

        const updatedItems = costItems.filter((_, i) => i !== index);
        setCostItems(updatedItems);
        if (editingIndex === index) {
            handleCancelEdit();
        } else if (editingIndex > index) {
            setEditingIndex(prev => prev - 1);
        }
    };

    const convertQuantityToBaseUnit = (
        quantity,
        uom,
        new_uom,
        kgVariations,
        gramVariations,
        literVariations,
        milliliterVariations
    ) => {
        let converted = parseFloat(quantity);

        if (new_uom && uom !== new_uom) {
            // If the original was kg and we converted to g, now convert back to kg
            if ((kgVariations.includes(uom.toLowerCase())) &&
                (gramVariations.includes(new_uom.toLowerCase()))) {
                converted = converted / 1000; // g → kg
            }
            // If the original was g and we converted to kg, now convert back to g
            else if ((gramVariations.includes(uom.toLowerCase())) &&
                (kgVariations.includes(new_uom.toLowerCase()))) {
                converted = converted * 1000; // kg → g
            }
            // If the original was ltr and we converted to ml, now convert back to ltr
            else if ((literVariations.includes(uom.toLowerCase())) &&
                (milliliterVariations.includes(new_uom.toLowerCase()))) {
                converted = converted / 1000; // ml → ltr
            }
            // If the original was ml and we converted to ltr, now convert back to ml
            else if ((milliliterVariations.includes(uom.toLowerCase())) &&
                (literVariations.includes(new_uom.toLowerCase()))) {
                converted = converted * 1000; // ltr → ml
            }
        }

        return converted;
    };

    const showSaveConfirmation = useCallback(() => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save this sub-recipe?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            try {
                                const totalCost = costItems.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0).toFixed(2);

                                const payload = {
                                    token: token,
                                    name: formData.name,
                                    outlet: selectedOutlet,
                                    uom: formData.uom,
                                    unit: formData.unit,
                                    costprice: parseFloat(totalCost),
                                    items: costItems.map(item => ({
                                        name: item.name,
                                        rate: parseFloat(item.rate) || 0,
                                        uom: item.itemUom,
                                        unit: item.itemUnit,
                                        new_uom: item.new_uom,
                                        quantity: convertQuantityToBaseUnit(
                                            item.quantity,
                                            item.uom,
                                            item.new_uom,
                                            kgVariations,
                                            gramVariations,
                                            literVariations,
                                            milliliterVariations
                                        ).toString(),
                                        cost: parseFloat(item.cost) || 0
                                    }))
                                };

                                await axios.post(`${baseUrl}/sub-recipe`, payload);
                                toast.success("Sub-recipe saved successfully!");

                                setCostItems([]);
                                setFormData({
                                    name: '',
                                    uom: '',
                                    unit: '',
                                    costItem: '',
                                    rate: '',
                                    itemUnit: '',
                                    itemUom: '',
                                    quantity: '',
                                    new_uom: '',
                                });

                            } catch (error) {
                                console.error('Error saving sub-recipe:', error);
                                toast.error('Failed to save sub-recipe');
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-subrecipe-confirmation'
            }
        );
    }, [baseUrl, costItems, formData.name, formData.uom, formData.unit, selectedOutlet, token]);

    const handleSave = () => {
        if (costItems.length === 0) {
            toast.error('Please add at least one ingredient');
            return;
        }
        if (!formData.name || !formData.uom || !formData.unit) {
            toast.error('Please fill all sub-recipe details');
            return;
        }
        showSaveConfirmation();
    };


    const itemOptions = useMemo(() =>
        unitDefinitions.map(item => ({
            value: item.name,
            label: item.name
        })),
        [unitDefinitions]
    );

    const totalCost = useMemo(() =>
        costItems.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0).toFixed(2),
        [costItems]
    );

    const showConversionOptions = useMemo(() =>
        ['kg', 'g', 'ltr', 'ml'].includes(formData.itemUom),
        [formData.itemUom]
    );

    const getUomOptions = useCallback((uom) => {
        const options = [];
        const lowerUom = uom?.toLowerCase();
        let defaultOption = null;

        if (kgVariations.includes(lowerUom) || gramVariations.includes(lowerUom)) {
            options.push(
                { value: 'g', label: 'g' },
                { value: 'kg', label: 'kg' }
            );
            defaultOption = 'g';
        } else if (literVariations.includes(lowerUom) || milliliterVariations.includes(lowerUom)) {
            options.push(
                { value: 'ml', label: 'ml' },
                { value: 'ltr', label: 'ltr' }
            );
            defaultOption = 'ml';
        }

        return { options, defaultOption };
    }, [kgVariations, gramVariations, literVariations, milliliterVariations]);

    const { options: uomOptions, defaultOption: defaultUom } = useMemo(() =>
        getUomOptions(formData.itemUom),
        [formData.itemUom, getUomOptions]
    );

    useEffect(() => {
        if (uomOptions.length > 0 && !formData.new_uom && formData.itemUom) {
            setFormData(prev => ({
                ...prev,
                new_uom: defaultUom
            }));
        }
    }, [uomOptions, formData.new_uom, formData.itemUom, defaultUom]);

    return (
        <div className='min-h-screen flex flex-col'>
            <Navbar />

            <div className='flex-grow'>
                <div className="max-w-5xl mx-auto mt-5 text-sm">
                    <div className="flex justify-center text-center mb-5">
                        <SelectSearchInput
                            token={token}
                            setToken={setToken}
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                        />
                    </div>

                    <h1 className="text-xl font-bold mb-4">Sub Recipe Cost Calculation</h1>

                    <Form onSubmit={handleAddCostItem} className="mb-8 bg-gray-50 p-4 rounded-lg">
                        <Row className="mb-4">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Sub Recipe Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        onKeyDown={(e) => handleKeyDown(e, recipeUomRef)}
                                        required
                                        ref={recipeNameRef}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>Unit</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="unit"
                                        value={formData.unit}
                                        onChange={handleInputChange}
                                        onKeyDown={(e) => handleKeyDown(e, ingredientSelectRef)}
                                        required
                                        ref={recipeUnitRef}
                                        min="0"
                                        step="any"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>UOM</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="uom"
                                        value={formData.uom}
                                        onChange={handleInputChange}
                                        onKeyDown={(e) => handleKeyDown(e, recipeUnitRef)}
                                        required
                                        ref={recipeUomRef}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {formData.name && (
                            <>
                                <Row className="mb-4">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Ingredient</Form.Label>
                                            <Select
                                                ref={ingredientSelectRef}
                                                name="costItem"
                                                value={formData.costItem ? { value: formData.costItem, label: formData.costItem } : null}
                                                onChange={handleCostItemSelect}
                                                options={itemOptions}
                                                placeholder="Select an ingredient"
                                                required
                                                isSearchable
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label>Rate</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="rate"
                                                step="any"
                                                value={formData.rate}
                                                readOnly
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label>Unit</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="itemUnit"
                                                value={
                                                    formData.itemUnit && formData.itemUom
                                                        ? `${formData.itemUnit} (${formData.itemUom})`
                                                        : ''
                                                }
                                                readOnly
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label>Quantity</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="quantity"
                                                value={formData.quantity}
                                                onChange={handleInputChange}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        if (showConversionOptions && uomOptions.length > 0) {
                                                            if (uomSelectRef.current) {
                                                                uomSelectRef.current.focus();
                                                            }
                                                        } else {
                                                            handleAddCostItem(e);
                                                        }
                                                    }
                                                }}
                                                required
                                                step="any"
                                                min="0"
                                                ref={quantityRef}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label>UOM</Form.Label>
                                            {showConversionOptions && uomOptions.length > 0 ? (
                                                <Select
                                                    ref={uomSelectRef}
                                                    options={uomOptions}
                                                    value={formData.new_uom
                                                        ? uomOptions.find(option => option.value === formData.new_uom)
                                                        : (uomOptions.length > 0 ? uomOptions[0] : null)}
                                                    onChange={handleUomChange}
                                                    placeholder="Select UOM"
                                                    className="basic-single"
                                                    classNamePrefix="select"
                                                    menuPortalTarget={document.body}
                                                    onKeyDown={(e) => handleKeyDown(e, null)}
                                                    styles={{
                                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                                    }}
                                                    isSearchable={false}
                                                />
                                            ) : (
                                                <Form.Control
                                                    type="text"
                                                    name="itemUom"
                                                    value={formData.itemUom || ''}
                                                    readOnly
                                                />
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="flex gap-2">
                                    <Button variant="primary" type="submit">
                                        <div className='flex justify-center items-center gap-2'>
                                            {editingIndex !== null ? <FaSave /> : <FaPlus />}
                                            {editingIndex !== null ? 'Update' : 'Add'}
                                        </div>
                                    </Button>
                                    {editingIndex !== null && (
                                        <Button variant="secondary" onClick={handleCancelEdit}>
                                            <div className='flex justify-center items-center gap-2'>
                                                <FaTimes />
                                                Cancel
                                            </div>
                                        </Button>
                                    )}
                                </div>
                            </>
                        )}
                    </Form>

                    {costItems.length > 0 && (
                        <div className="mb-8">
                            <Table striped bordered hover className="text-center">
                                <thead>
                                    <tr>
                                        <th>Ingredient</th>
                                        <th>Rate</th>
                                        <th>Unit</th>
                                        <th>UOM</th>
                                        <th>Quantity</th>
                                        <th>New UOM</th>
                                        <th>Cost</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {costItems.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.name}</td>
                                            <td>{item.rate}</td>
                                            <td>{item.itemUnit}</td>
                                            <td>{item.itemUom}</td>
                                            <td>{item.quantity}</td>
                                            <td>{item.new_uom}</td>
                                            <td>{item.cost}</td>
                                            <td>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => handleEdit(index)}
                                                    className="mr-2"
                                                >
                                                    <FaEdit />
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(index)}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            <div className="bg-gray-100 p-4 rounded-lg mb-4">
                                <Row>
                                    <Col md={4}>
                                        <div className="font-bold">Total Cost: {totalCost}</div>
                                    </Col>
                                </Row>
                            </div>

                            <Button variant="success" onClick={handleSave}>
                                <div className='flex justify-center items-center gap-2'>
                                    Save Sub-Recipe
                                </div>
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default SubRecipeCost;

